import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { AgentFollowSignal } from './agent-ia.models';
import { AgentDataChange, AgentFollowPage, AgentFollower, AgentNavIntent } from './agent-follow';

export type { AgentTarget } from './agent-ia.models';
export type { AgentDataChange, AgentFollowPage, AgentNavIntent, AgentNavKind } from './agent-follow';
export { AGENT_FOLLOW_INFO, AGENT_PANEL_INFO } from './agent-follow';

// Follow mode (SPEC §8 `target`, follow mode): root service that plugs the pure AgentFollower into the router, the
// document and the timers. Pages (DPGF, plans, project dashboard) consume changes$ / register / userTookControl /
// takeHighlight; the agent panel feeds signal() from its state and calls flushPages() before send / answer / approve / undo.
// Timers and document listeners run outside Angular: a blocked intent re-checked every second triggers no change detection.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
// Bootstrap modal, PrimeNG dialog / confirm dialog / modal drawer mask.
const modalOpen = () => typeof document !== 'undefined' && (document.body.classList.contains('modal-open') || Boolean(document.querySelector('.p-overlay-mask')));

@Injectable({ providedIn: 'root' })
export class AgentFollowService implements OnDestroy {
  readonly changes$: Observable<AgentDataChange>;
  readonly viewChanged$: Observable<void>;          // the bubble refreshes its view
  readonly engine: AgentFollower;
  private readonly changes = new Subject<AgentDataChange>();
  private readonly view = new Subject<void>();
  private readonly subscription: Subscription;
  private readonly activity = (event: Event) => {
    const target = event.target as Element | null;
    if (target?.closest?.('app-agent-ia')) return;   // the panel and the bubble are not the workspace
    this.engine.userActivity(event.type === 'pointerdown' || event.type === 'keydown');
  };
  private readonly visibility = () => this.engine.visibilityChanged();

  constructor(private router: Router, private zone: NgZone) {
    this.changes$ = this.changes.asObservable();
    this.viewChanged$ = this.view.asObservable();
    this.engine = new AgentFollower({
      url: () => this.router.url,
      navigate: (commands, extras) => this.zone.run(() => this.router.navigate(commands, extras)),
      visible: () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
      modalOpen,
      now: () => Date.now(),
      timers: { setTimeout: (callback, ms) => this.zone.runOutsideAngular(() => setTimeout(callback, ms)), clearTimeout: handle => clearTimeout(handle as any) },
      changed: () => this.view.next(),
      emit: change => this.zone.run(() => this.changes.next(change))
    });
    this.subscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.engine.navigationStart({ id: event.id, url: event.url, popstate: event.navigationTrigger === 'popstate', info: this.router.getCurrentNavigation()?.extras.info });
      } else if (event instanceof NavigationEnd) this.engine.navigationEnd(true);
      else if (event instanceof NavigationCancel || event instanceof NavigationError || event instanceof NavigationSkipped) this.engine.navigationEnd(false);
    });
    if (typeof document !== 'undefined') this.zone.runOutsideAngular(() => {
      for (const type of ACTIVITY_EVENTS) document.addEventListener(type, this.activity, { capture: true, passive: true });
      document.addEventListener('visibilitychange', this.visibility);
    });
  }

  // ---- Interface of the pages (WP4) -------------------------------------------------------

  // ngOnInit; call the returned function in ngOnDestroy.
  register(page: AgentFollowPage): () => void { return this.engine.register(page); }
  // The user picked another lot himself: following pauses until « Reprendre le suivi » or the next run.
  userTookControl(): void { this.engine.userTookControl(); }
  // Post ids to highlight in this lot, once.
  takeHighlight(dpgfId: string, lotId: string): string[] { return this.engine.takeHighlight(dpgfId, lotId); }

  // ---- Agent panel and bubble -------------------------------------------------------------

  signal(signal: AgentFollowSignal) { this.engine.signal(signal); }
  reset() { this.engine.reset(); }
  flushPages(): Promise<boolean> { return this.engine.flushPages(); }
  modalOpen() { return modalOpen(); }
  open(intent: AgentNavIntent | null) { return this.engine.open(intent); }

  ngOnDestroy() {
    this.subscription.unsubscribe(); this.engine.dispose();
    if (typeof document === 'undefined') return;
    for (const type of ACTIVITY_EVENTS) document.removeEventListener(type, this.activity, { capture: true });
    document.removeEventListener('visibilitychange', this.visibility);
  }
}
